import React, { useState } from "react";
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Input,
    Label,
    Button,
    Table,
    CardHeader,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap";
import { MoreVertical, Edit, Trash } from "lucide-react"
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const DailyCollectionReport = () => {
    // State for date inputs
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    // State for search input
    const [searchDate, setSearchDate] = useState("");

    // State to store filtered daily collection reports
    const [filteredReports, setFilteredReports] = useState([]);

    // State to track if the form has been submitted
    const [isSubmitted, setIsSubmitted] = useState(false);

    // State for dropdown toggle
    const [dropdownOpen, setDropdownOpen] = useState(null);

    // State for delete modal
    const [deleteModal, setDeleteModal] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);

    // Dummy data for daily collection reports
    const dailyCollectionReports = [
        { id: 1, date: "2023-10-01", totalTransactions: 10, amount: 50000 },
        { id: 2, date: "2023-10-02", totalTransactions: 15, amount: 75000 },
        { id: 3, date: "2023-10-03", totalTransactions: 8, amount: 40000 },
        { id: 4, date: "2023-10-04", totalTransactions: 12, amount: 60000 },
        { id: 5, date: "2023-10-05", totalTransactions: 20, amount: 100000 },
        { id: 6, date: "2025-02-01", totalTransactions: 10, amount: 50000 },
        { id: 7, date: "2025-02-02", totalTransactions: 15, amount: 75000 },
        { id: 8, date: "2025-02-03", totalTransactions: 8, amount: 40000 },
        { id: 9, date: "2025-01-04", totalTransactions: 12, amount: 60000 },
        { id: 10, date: "2025-01-05", totalTransactions: 20, amount: 100000 },
        { id: 11, date: "2025-03-01", totalTransactions: 10, amount: 50000 },
        { id: 12, date: "2025-03-02", totalTransactions: 15, amount: 75000 },
        { id: 13, date: "2025-03-03", totalTransactions: 8, amount: 40000 },
        { id: 14, date: "2025-01-14", totalTransactions: 12, amount: 60000 },
        { id: 15, date: "2025-02-05", totalTransactions: 20, amount: 100000 },
    ];

    // Handle form submission
    const handleSubmit = () => {
        // Filter reports based on the selected date range
        const filtered = dailyCollectionReports.filter((report) => {
            const reportDate = new Date(report.date);
            const fromDate = new Date(dateFrom);
            const toDate = new Date(dateTo);
            return reportDate >= fromDate && reportDate <= toDate;
        });
        setFilteredReports(filtered);
        setIsSubmitted(true); // Set submitted state to true
    };

    // Handle search by date
    const handleSearch = (e) => {
        const searchValue = e.target.value;
        setSearchDate(searchValue);

        if (searchValue) {
            const filtered = dailyCollectionReports.filter((report) => report.date === searchValue);
            setFilteredReports(filtered);
        } else {
            // Reset to the original filtered reports based on date range
            const filtered = dailyCollectionReports.filter((report) => {
                const reportDate = new Date(report.date);
                const fromDate = new Date(dateFrom);
                const toDate = new Date(dateTo);
                return reportDate >= fromDate && reportDate <= toDate;
            });
            setFilteredReports(filtered);
        }
    };

    // Toggle dropdown
    const toggleDropdown = (id) => {
        setDropdownOpen(dropdownOpen === id ? null : id);
    };

    // Toggle delete modal
    const toggleDeleteModal = () => {
        setDeleteModal(!deleteModal);
    };

    // Handle delete action
    const handleDelete = () => {
        if (reportToDelete) {
            // Perform delete operation (e.g., API call)
            console.log("Deleting report:", reportToDelete);
            setFilteredReports(filteredReports.filter((report) => report.id !== reportToDelete.id));
            setReportToDelete(null);
            toggleDeleteModal();
        }
    };

    // Calculate total amount
    const totalAmount = filteredReports.reduce((sum, report) => sum + report.amount, 0);

    // Breadcrumb items
    const breadcrumbItems = [
        { title: "Reports", link: "/" },
        { title: "Finance Reports", link: "/" },
        { title: "Daily Collection Report", link: "/admin/daily-collection-report" },
    ];

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Daily Collection Report" breadcrumbItems={breadcrumbItems} />
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader className="d-flex justify-content-between align-items-center flex-wrap">
                                    <h4 className="card-title mb-0 flex-grow-1">Daily Collection Report</h4>
                                </CardHeader>
                                <CardBody>
                                    <Row>
                                        {/* Date From Input */}
                                        <Col lg={4}>
                                            <Label>Date From</Label>
                                            <Input
                                                type="date"
                                                value={dateFrom}
                                                onChange={(e) => setDateFrom(e.target.value)}
                                            />
                                        </Col>

                                        {/* Date To Input */}
                                        <Col lg={4}>
                                            <Label>Date To</Label>
                                            <Input
                                                type="date"
                                                value={dateTo}
                                                onChange={(e) => setDateTo(e.target.value)}
                                            />
                                        </Col>

                                        {/* Submit Button */}
                                        <Col lg={4} className="d-flex align-items-end">
                                            <Button color="primary" onClick={handleSubmit}>
                                                Submit
                                            </Button>
                                        </Col>
                                    </Row>

                                    {/* Show Search Bar and Table only after submission */}
                                    {isSubmitted && (
                                        <>
                                            {/* Search Input */}
                                            <Row className="mt-3">
                                                <Col lg={4}>
                                                    <Label>Search by Date</Label>
                                                    <Input
                                                        type="date"
                                                        value={searchDate}
                                                        onChange={handleSearch}
                                                    />
                                                </Col>
                                            </Row>

                                            {/* Daily Collection Report Table */}
                                            <Row className="mt-4">
                                                <Col lg={12}>
                                                    {filteredReports.length > 0 ? (
                                                        <>
                                                            <Table striped>
                                                                <thead>
                                                                    <tr>
                                                                        <th>Date</th>
                                                                        <th>Total Transactions</th>
                                                                        <th>Amount</th>
                                                                        <th>Actions</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {filteredReports.map((report) => (
                                                                        <tr key={report.id}>
                                                                            <td>{report.date}</td>
                                                                            <td>{report.totalTransactions}</td>
                                                                            <td>{report.amount}</td>
                                                                            <td>
                                                                                <Dropdown
                                                                                    isOpen={dropdownOpen === report.id}
                                                                                    toggle={() => toggleDropdown(report.id)}
                                                                                >
                                                                                    <DropdownToggle tag="button" className="btn btn-light">
                                                                                        <MoreVertical size={20} />
                                                                                    </DropdownToggle>
                                                                                    <DropdownMenu>
                                                                                        <DropdownItem>
                                                                                            <Edit size={16} className="me-2" />
                                                                                            Edit
                                                                                        </DropdownItem>
                                                                                        <DropdownItem
                                                                                            onClick={() => {
                                                                                                setReportToDelete(report);
                                                                                                toggleDeleteModal();
                                                                                            }}
                                                                                            className="text-danger"
                                                                                        >
                                                                                            <Trash size={16} className="me-2" />
                                                                                            Delete
                                                                                        </DropdownItem>
                                                                                    </DropdownMenu>
                                                                                </Dropdown>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </Table>

                                                            {/* Total Amount Row */}
                                                            <div className="mt-3 text-end">
                                                                <strong>Total Amount: {totalAmount}</strong>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="text-center">
                                                            <p>No Record Found</p>
                                                        </div>
                                                    )}
                                                </Col>
                                            </Row>
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModal} centered toggle={toggleDeleteModal}>
                <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
                <ModalBody>
                    Are you sure you want to delete this report?
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleDeleteModal}>
                        Cancel
                    </Button>
                    <Button color="danger" onClick={handleDelete}>
                        Delete
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default DailyCollectionReport;